import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { MapPin, Navigation, Building2, Hospital, Car, Plane } from 'lucide-react';
import { Badge } from './ui/badge';

const InteractiveMap = () => {
  const { t } = useTranslation();
  const [selectedPoi, setSelectedPoi] = useState(null);

  const pointsOfInterest = [
    {
      id: 1,
      name: 'Seeschloss-Hotel',
      type: 'property',
      distance: '0 km',
      description: t('map.poi.hotel'),
      icon: Building2,
      color: '#C9A961'
    },
    {
      id: 2,
      name: t('map.poi.clinicsName'),
      type: 'healthcare',
      distance: '3 km',
      description: t('map.poi.clinicsDesc'),
      icon: Hospital,
      color: '#2C5F66'
    },
    {
      id: 3,
      name: 'Eutin',
      type: 'city',
      distance: '5 km',
      description: t('map.poi.eutinDesc'),
      icon: Building2,
      color: '#0A2540'
    },
    {
      id: 4,
      name: 'Lübeck (UNESCO)',
      type: 'city',
      distance: '25 km',
      description: t('map.poi.luebeckDesc'),
      icon: Building2,
      color: '#0A2540'
    },
    {
      id: 5,
      name: t('map.poi.balticName'),
      type: 'beach',
      distance: '35 km',
      description: t('map.poi.balticDesc'),
      icon: MapPin,
      color: '#4A7C85'
    },
    {
      id: 6,
      name: 'Hamburg',
      type: 'city',
      distance: '80 km',
      description: t('map.poi.hamburgDesc'),
      icon: Plane,
      color: '#1E3A5F'
    },
    {
      id: 7,
      name: t('map.poi.highwayName'),
      type: 'highway',
      distance: '15 km',
      description: t('map.poi.highwayDesc'),
      icon: Car,
      color: '#2C5F66'
    }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <Card className="border-slate-200 shadow-xl overflow-hidden">
        <CardContent className="p-0">
          <div className="grid md:grid-cols-2">
          {/* Map Area */}
          <div className="relative aspect-square md:aspect-auto bg-gradient-to-br from-[#E8ECF0] to-[#F8F9FA]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative w-full h-full p-8">
                {/* Central Property */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-[#C9A961] flex items-center justify-center shadow-2xl animate-pulse">
                      <Building2 className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                      <Badge className="bg-[#C9A961] text-white">Seeschloss-Hotel</Badge>
                    </div>
                  </div>
                </div>

                {/* POI Markers */}
                <div className="absolute top-[30%] left-[45%] cursor-pointer" onClick={() => setSelectedPoi(2)}>
                  <div className="w-10 h-10 rounded-full bg-[#2C5F66] flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    <Hospital className="w-5 h-5 text-white" />
                  </div>
                </div>

                <div className="absolute top-[25%] left-[35%] cursor-pointer" onClick={() => setSelectedPoi(3)}>
                  <div className="w-8 h-8 rounded-full bg-[#0A2540] flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                </div>

                <div className="absolute top-[15%] left-[20%] cursor-pointer" onClick={() => setSelectedPoi(4)}>
                  <div className="w-8 h-8 rounded-full bg-[#0A2540] flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                </div>

                <div className="absolute top-[20%] right-[25%] cursor-pointer" onClick={() => setSelectedPoi(5)}>
                  <div className="w-8 h-8 rounded-full bg-[#4A7C85] flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                </div>

                <div className="absolute top-[10%] left-[10%] cursor-pointer" onClick={() => setSelectedPoi(6)}>
                  <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    <Plane className="w-4 h-4 text-white" />
                  </div>
                </div>

                <div className="absolute bottom-[25%] left-[40%] cursor-pointer" onClick={() => setSelectedPoi(7)}>
                  <div className="w-8 h-8 rounded-full bg-[#2C5F66] flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    <Car className="w-4 h-4 text-white" />
                  </div>
                </div>

                {/* Decorative circles */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full border-2 border-[#C9A961]/30"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full border-2 border-[#C9A961]/20"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full border-2 border-[#C9A961]/10"></div>
              </div>
            </div>
            
            <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-md rounded-lg px-3 py-2 text-xs text-slate-600">
              <div className="flex items-center space-x-2">
                <Navigation className="w-4 h-4" />
                <span>{t('map.interactiveOverview')}</span>
              </div>
            </div>
          </div>

          {/* Info Panel */}
          <div className="bg-white p-8">
            <h3 className="text-2xl font-bold text-[#0A2540] mb-6">{t('map.title')}</h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {pointsOfInterest.map((poi) => {
                const Icon = poi.icon;
                const isSelected = selectedPoi === poi.id;
                return (
                  <div 
                    key={poi.id}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                      isSelected 
                        ? 'border-[#C9A961] bg-[#C9A961]/5 shadow-md' 
                        : 'border-slate-200 hover:border-[#C9A961]/50 hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedPoi(poi.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: poi.color }}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-[#0A2540]">{poi.name}</h4>
                          <Badge variant="outline" className="text-xs">{poi.distance}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">{poi.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 leading-relaxed">
                <strong>{t('map.footerTitle')}</strong> {t('map.footerText')}
              </p>
            </div>
          </div>
        </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InteractiveMap;